import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useToast } from '@/components/common/Toast';
import layerAdmin, {
  labelOf,
  LayerChange,
  LayerDetail,
  LayerField,
  LayerStyle,
  Measure,
  Published,
  StyleClass,
} from '@/services/layerAdmin.service';

/**
 * Our layers, set up here instead of in GeoServer's screens (Veramenta docs/en/60 sec.10): what a layer is
 * called and where the map lists it, how it looks, what its columns are called and which are money or
 * personal, what a catchment adds up from it. Saving changes the registry, and GeoServer is told.
 */

const LANGUAGES = ['en', 'tr', 'nl', 'de'];
const TABS = ['general', 'style', 'fields', 'analysis', 'geoserver'] as const;
type Tab = (typeof TABS)[number];

const input = 'w-full px-3 py-2 border border-gn-light rounded bg-gn-surface text-gn-primary';
const button = 'px-3 py-2 border border-gn-light rounded hover:bg-gn-surface disabled:opacity-50';
const primary = 'px-4 py-2 bg-gn-blue text-gn-inverse rounded disabled:opacity-50';

interface Problem {
  code?: string;
  where?: string;
  message?: string;
}

const LayerManagement: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const lang = (i18n.language || 'en').substring(0, 2);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('general');
  const [draft, setDraft] = useState<LayerChange>({});
  const [fields, setFields] = useState<LayerField[]>([]);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [published, setPublished] = useState<Published | null>(null);
  const [sldPreview, setSldPreview] = useState<string | null>(null);

  const { data: layers = [], isLoading } = useQuery({ queryKey: ['adminLayers'], queryFn: () => layerAdmin.layers() });
  const { data: vocabulary } = useQuery({ queryKey: ['adminVocabulary'], queryFn: () => layerAdmin.vocabulary() });
  const { data: detail } = useQuery({
    queryKey: ['adminLayer', selected],
    queryFn: () => layerAdmin.layer(selected!),
    enabled: !!selected,
  });

  useEffect(() => {
    if (!detail) return;
    const m = detail.map;
    setDraft({
      labels: { ...m.labels },
      menu: m.menu ?? '',
      position: m.position ?? 0,
      kind: m.kind ?? '',
      visible: m.visible ?? false,
      minScale: m.minScale ?? null,
      maxScale: m.maxScale ?? null,
      style: m.style ? { ...m.style, classes: [...(m.style.classes ?? [])] } : { mode: 'simple' },
      info: [...(m.info ?? [])],
      label: m.label ?? null,
      analysis: [...(m.analysis ?? [])],
    });
    setFields(detail.fields.map((f) => ({ ...f, labels: { ...f.labels } })));
    setProblems([]);
    setPublished(detail.geoserver ?? null);
    setSldPreview(null);
  }, [detail]);

  const grouped = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const groups = new Map<string, typeof layers>();
    for (const l of layers) {
      const text = `${l.name} ${labelOf(l.labels, lang)} ${l.typeKey}`.toLowerCase();
      if (f && !text.includes(f)) continue;
      const menu = l.menu?.split('/')[0] || '—';
      groups.set(menu, [...(groups.get(menu) ?? []), l]);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [layers, filter, lang]);

  const save = useMutation({
    mutationFn: () => {
      const before = detail!.fields;
      const changedFields = fields
        .filter((f) => {
          const was = before.find((b) => b.key === f.key);
          return !was || JSON.stringify(was.labels) !== JSON.stringify(f.labels) || was.fieldClass !== f.fieldClass;
        })
        .map((f) => ({ key: f.key, labels: f.labels, fieldClass: f.fieldClass }));
      const style = draft.style && draft.style.mode !== 'named' && draft.style.mode !== 'sld'
        ? { ...draft.style, sld: null } : draft.style;
      return layerAdmin.change(selected!, {
        ...draft,
        style,
        menu: draft.menu || null,
        kind: draft.kind || null,
        fields: changedFields,
      });
    },
    onSuccess: (d: LayerDetail) => {
      queryClient.setQueryData(['adminLayer', selected], d);
      queryClient.invalidateQueries({ queryKey: ['adminLayers'] });
      queryClient.invalidateQueries({ queryKey: ['layers'] });
      setProblems([]);
      setPublished(d.geoserver ?? null);
      addToast(t('layerAdmin.saved', { defaultValue: 'Layer saved' }), 'success');
    },
    onError: (e: AxiosError<{ problems?: Problem[]; message?: string }>) => {
      setProblems(e.response?.data?.problems ?? [{ message: e.response?.data?.message ?? e.message }]);
      addToast(t('layerAdmin.save_failed', { defaultValue: 'The layer could not be saved' }), 'error');
    },
  });

  const publish = useMutation({
    mutationFn: () => layerAdmin.publish(selected!),
    onSuccess: (p) => {
      setPublished(p);
      addToast(p.problems.length ? t('layerAdmin.publish_problems', { defaultValue: 'GeoServer reported problems' })
        : t('layerAdmin.published', { defaultValue: 'GeoServer updated' }), p.problems.length ? 'error' : 'success');
    },
    onError: (e: AxiosError<{ message?: string }>) => addToast(e.response?.data?.message ?? e.message, 'error'),
  });

  const preview = useMutation({
    mutationFn: () => layerAdmin.sld(selected!, draft.style ?? { mode: 'simple' }),
    onSuccess: (sld) => setSldPreview(sld || ''),
  });

  const set = <K extends keyof LayerChange>(key: K, value: LayerChange[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const setStyle = (patch: Partial<LayerStyle>) =>
    setDraft((d) => ({ ...d, style: { ...(d.style ?? { mode: 'simple' }), ...patch } as LayerStyle }));
  const fieldOptions = fields.filter((f) => f.type !== 'geometry');

  return (
    <div className="flex gap-6 min-h-0">
      <div className="w-80 shrink-0">
        <h2 className="text-xl font-bold mb-1">{t('layerAdmin.title', { defaultValue: 'Layers' })}</h2>
        <p className="text-sm text-gn-muted mb-3">
          {t('layerAdmin.hint', { defaultValue: 'Every platform layer as the registry describes it. Changes here are published to GeoServer.' })}
        </p>
        <input className={`${input} mb-3`} placeholder={t('layerAdmin.filter', { defaultValue: 'Filter layers' })}
               value={filter} onChange={(e) => setFilter(e.target.value)} />
        {isLoading && <p className="text-sm text-gn-muted">…</p>}
        <div className="space-y-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
          {grouped.map(([menu, group]) => (
            <div key={menu}>
              <div className="text-xs uppercase tracking-wide text-gn-muted mb-1">{menu}</div>
              <ul className="space-y-1">
                {group.map((l) => (
                  <li key={l.key}>
                    <button type="button" onClick={() => { setSelected(l.key); setTab('general'); }}
                            className={`w-full text-left px-3 py-2 rounded ${selected === l.key ? 'bg-gn-blue text-gn-inverse' : 'hover:bg-gn-surface'}`}>
                      <div className="font-medium truncate">{labelOf(l.labels, lang, l.name)}</div>
                      <div className="text-xs opacity-75 truncate">
                        {l.workspace}:{l.name} · {l.binding === 'dynamic' ? t('layerAdmin.in_store', { defaultValue: 'in the store' })
                          : t('layerAdmin.native', { defaultValue: 'own table' })} · {t('layerAdmin.companies_count', { count: l.companies, defaultValue: '{{count}} companies' })}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {selected && detail && (
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3 gap-4">
            <div>
              <h3 className="text-lg font-bold">{labelOf(draft.labels, lang, detail.name)}</h3>
              <div className="text-xs text-gn-muted">
                {detail.typeKey} · {detail.workspace}:{detail.name} · #{detail.number} · {detail.geometry}
                {detail.map.source?.table ? ` · ${detail.map.source.schema}.${detail.map.source.table}` : ''}
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" className={button} disabled={publish.isPending} onClick={() => publish.mutate()}>
                {t('layerAdmin.publish', { defaultValue: 'Publish to GeoServer' })}
              </button>
              <button type="button" className={primary} disabled={save.isPending} onClick={() => save.mutate()}>
                {t('buttons.save', { defaultValue: 'Save' })}
              </button>
            </div>
          </div>

          {problems.length > 0 && (
            <div className="border border-red-400 bg-red-50 text-red-800 rounded p-3 mb-3 text-sm">
              {problems.map((p, i) => <div key={i}>{p.where ? <b>{p.where}: </b> : null}{p.message ?? p.code}</div>)}
            </div>
          )}

          <div className="flex gap-1 border-b border-gn-light mb-4">
            {TABS.map((x) => (
              <button key={x} type="button" onClick={() => setTab(x)}
                      className={`px-3 py-2 -mb-px border-b-2 ${tab === x ? 'border-gn-blue font-medium' : 'border-transparent text-gn-muted'}`}>
                {t(`layerAdmin.tab_${x}`, { defaultValue: x })}
              </button>
            ))}
          </div>

          {tab === 'general' && (
            <div className="grid grid-cols-2 gap-4 max-w-3xl">
              {LANGUAGES.map((l) => (
                <label key={l} className="block">
                  <span className="text-sm">{t('layerAdmin.name_in', { lang: l.toUpperCase(), defaultValue: 'Name ({{lang}})' })}</span>
                  <input className={input} value={draft.labels?.[l] ?? ''}
                         onChange={(e) => set('labels', { ...(draft.labels ?? {}), [l]: e.target.value })} />
                </label>
              ))}
              <label className="block">
                <span className="text-sm">{t('layerAdmin.menu', { defaultValue: 'Menu' })}</span>
                <input className={input} list="gx-menus" value={draft.menu ?? ''} onChange={(e) => set('menu', e.target.value)}
                       placeholder="thematics/District" />
                <datalist id="gx-menus">
                  {Object.keys(vocabulary?.menus ?? {}).flatMap((m) =>
                    [m, ...Object.keys(vocabulary?.subMenus ?? {}).map((s) => `${m}/${s}`)]).map((m) => <option key={m} value={m} />)}
                </datalist>
              </label>
              <label className="block">
                <span className="text-sm">{t('layerAdmin.kind', { defaultValue: 'Kind' })}</span>
                <select className={input} value={draft.kind ?? ''} onChange={(e) => set('kind', e.target.value)}>
                  <option value="">—</option>
                  {(vocabulary?.kinds ?? []).map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm">{t('layerAdmin.position', { defaultValue: 'Position in its menu' })}</span>
                <input type="number" className={input} value={draft.position ?? 0}
                       onChange={(e) => set('position', Number(e.target.value))} />
              </label>
              <label className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={!!draft.visible} onChange={(e) => set('visible', e.target.checked)} />
                <span className="text-sm">{t('layerAdmin.visible', { defaultValue: 'Shown when the map opens' })}</span>
              </label>
              <label className="block">
                <span className="text-sm">{t('layerAdmin.min_scale', { defaultValue: 'Shown from scale 1:' })}</span>
                <input type="number" className={input} value={draft.minScale ?? ''}
                       onChange={(e) => set('minScale', e.target.value === '' ? null : Number(e.target.value))} />
              </label>
              <label className="block">
                <span className="text-sm">{t('layerAdmin.max_scale', { defaultValue: 'Shown up to scale 1:' })}</span>
                <input type="number" className={input} value={draft.maxScale ?? ''}
                       onChange={(e) => set('maxScale', e.target.value === '' ? null : Number(e.target.value))} />
              </label>
              <div className="col-span-2">
                <span className="text-sm">{t('layerAdmin.place_columns', { defaultValue: 'Columns that name where a point is (analysis reports)' })}</span>
                <div className="grid grid-cols-3 gap-1 mt-1">
                  {fieldOptions.map((f) => (
                    <label key={f.key} className="inline-flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={draft.info?.includes(f.key) ?? false}
                             onChange={(e) => set('info', e.target.checked ? [...(draft.info ?? []), f.key]
                               : (draft.info ?? []).filter((x) => x !== f.key))} />
                      {f.column}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'style' && (
            <StyleEditor style={draft.style ?? { mode: 'simple' }} fields={fieldOptions} geometry={detail.geometry}
                         label={draft.label ?? null} onLabel={(v) => set('label', v)} onChange={setStyle}
                         onPreview={() => preview.mutate()} preview={sldPreview} t={t} />
          )}

          {tab === 'fields' && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gn-muted">
                  <th className="py-1 pr-2">{t('layerAdmin.column', { defaultValue: 'Column' })}</th>
                  <th className="py-1 pr-2">{t('layerAdmin.type', { defaultValue: 'Type' })}</th>
                  {LANGUAGES.map((l) => <th key={l} className="py-1 pr-2">{l.toUpperCase()}</th>)}
                  <th className="py-1 pr-2">{t('layerAdmin.class', { defaultValue: 'Who sees it' })}</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f, i) => (
                  <tr key={f.key} className="border-t border-gn-light">
                    <td className="py-1 pr-2 font-mono">{f.column}</td>
                    <td className="py-1 pr-2">{f.type}{f.of ? ` (${f.of})` : ''}</td>
                    {LANGUAGES.map((l) => (
                      <td key={l} className="py-1 pr-2">
                        <input className={input} value={f.labels?.[l] ?? ''} disabled={f.type === 'geometry'}
                               onChange={(e) => setFields(fields.map((x, j) => j === i ? { ...x, labels: { ...x.labels, [l]: e.target.value } } : x))} />
                      </td>
                    ))}
                    <td className="py-1 pr-2">
                      <select className={input} value={f.fieldClass} disabled={f.type === 'geometry'}
                              onChange={(e) => setFields(fields.map((x, j) => j === i ? { ...x, fieldClass: e.target.value as LayerField['fieldClass'] } : x))}>
                        {(vocabulary?.fieldClasses ?? ['none']).map((c) => (
                          <option key={c} value={c}>{t(`layerAdmin.class_${c}`, { defaultValue: c })}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'analysis' && (
            <MeasuresEditor measures={draft.analysis ?? []} fields={fieldOptions} methods={vocabulary?.methods ?? []}
                            onChange={(m) => set('analysis', m)} t={t}
                            suggested={!!detail.analysisSuggested && (draft.analysis ?? []).length === 0}
                            onSuggest={async () => set('analysis', await layerAdmin.suggestedMeasures(selected!))} />
          )}

          {tab === 'geoserver' && (
            <div className="space-y-3 text-sm max-w-3xl">
              <p>{t('layerAdmin.geoserver_hint', { defaultValue: 'GeoServer draws this layer as the registry says. Publishing writes its title and style, and for a layer whose features are in the store, the view it is drawn from.' })}</p>
              <div><b>GeoServer:</b> {detail.map.geoserver}</div>
              <div><b>{t('layerAdmin.style', { defaultValue: 'Style' })}:</b> {detail.map.style?.mode}{detail.map.style?.mode === 'named' ? ` (${detail.map.style.sld})` : ''}</div>
              {published && (
                <div className="border border-gn-light rounded p-3">
                  {published.done.map((d, i) => <div key={i}>✓ {d}</div>)}
                  {published.problems.map((p, i) => <div key={i} className="text-red-700">✗ {p}</div>)}
                </div>
              )}
              {detail.sld && (
                <details>
                  <summary className="cursor-pointer">SLD</summary>
                  <pre className="text-xs whitespace-pre-wrap break-all bg-gn-surface p-2 rounded max-h-96 overflow-auto">{detail.sld}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type T = (key: string, options?: Record<string, unknown>) => string;

const StyleEditor: React.FC<{
  style: LayerStyle;
  fields: LayerField[];
  geometry: string;
  label: string | null;
  onLabel: (v: string | null) => void;
  onChange: (patch: Partial<LayerStyle>) => void;
  onPreview: () => void;
  preview: string | null;
  t: T;
}> = ({ style, fields, geometry, label, onLabel, onChange, onPreview, preview, t }) => {
  const classes = style.classes ?? [];
  const setClass = (i: number, patch: Partial<StyleClass>) =>
    onChange({ classes: classes.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex gap-4 items-end">
        <label className="block w-60">
          <span className="text-sm">{t('layerAdmin.style_mode', { defaultValue: 'How it is drawn' })}</span>
          <select className={input} value={style.mode} onChange={(e) => onChange({ mode: e.target.value as LayerStyle['mode'] })}>
            <option value="simple">{t('layerAdmin.mode_simple', { defaultValue: 'One colour' })}</option>
            <option value="classes">{t('layerAdmin.mode_classes', { defaultValue: 'Coloured by a column' })}</option>
            <option value="named">{t('layerAdmin.mode_named', { defaultValue: 'A style GeoServer has' })}</option>
            <option value="sld">{t('layerAdmin.mode_sld', { defaultValue: 'SLD written by hand' })}</option>
          </select>
        </label>
        <button type="button" className={button} onClick={onPreview}>{t('layerAdmin.preview_sld', { defaultValue: 'Show the SLD' })}</button>
      </div>

      {(style.mode === 'simple' || style.mode === 'classes') && (
        <div className="grid grid-cols-3 gap-4">
          <Colour label={t('layerAdmin.fill', { defaultValue: 'Fill' })} value={style.fill} onChange={(v) => onChange({ fill: v })} />
          <Colour label={t('layerAdmin.stroke', { defaultValue: 'Outline' })} value={style.stroke} onChange={(v) => onChange({ stroke: v })} />
          <label className="block">
            <span className="text-sm">{t('layerAdmin.opacity', { defaultValue: 'Opacity (0–1)' })}</span>
            <input type="number" step="0.1" min="0" max="1" className={input} value={style.fillOpacity ?? ''}
                   onChange={(e) => onChange({ fillOpacity: e.target.value === '' ? null : Number(e.target.value) })} />
          </label>
          <label className="block">
            <span className="text-sm">{t('layerAdmin.stroke_width', { defaultValue: 'Outline width (px)' })}</span>
            <input type="number" step="0.5" className={input} value={style.strokeWidth ?? ''}
                   onChange={(e) => onChange({ strokeWidth: e.target.value === '' ? null : Number(e.target.value) })} />
          </label>
          {geometry === 'point' && (
            <>
              <label className="block">
                <span className="text-sm">{t('layerAdmin.marker', { defaultValue: 'Marker' })}</span>
                <select className={input} value={style.marker ?? 'circle'} onChange={(e) => onChange({ marker: e.target.value })}>
                  {['circle', 'square', 'triangle', 'star', 'cross'].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm">{t('layerAdmin.size', { defaultValue: 'Size (px)' })}</span>
                <input type="number" className={input} value={style.size ?? ''}
                       onChange={(e) => onChange({ size: e.target.value === '' ? null : Number(e.target.value) })} />
              </label>
            </>
          )}
          <label className="block">
            <span className="text-sm">{t('layerAdmin.label_field', { defaultValue: 'Label' })}</span>
            <select className={input} value={label ?? ''} onChange={(e) => onLabel(e.target.value || null)}>
              <option value="">—</option>
              {fields.map((f) => <option key={f.key} value={f.key}>{f.column}</option>)}
            </select>
          </label>
          {label && <Colour label={t('layerAdmin.label_colour', { defaultValue: 'Label colour' })} value={style.labelColour}
                            onChange={(v) => onChange({ labelColour: v })} />}
        </div>
      )}

      {style.mode === 'classes' && (
        <div className="space-y-2">
          <label className="block w-80">
            <span className="text-sm">{t('layerAdmin.by', { defaultValue: 'Coloured by' })}</span>
            <select className={input} value={style.by ?? ''} onChange={(e) => onChange({ by: e.target.value })}>
              <option value="">—</option>
              {fields.map((f) => <option key={f.key} value={f.key}>{f.column}</option>)}
            </select>
          </label>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gn-muted">
                <th>{t('layerAdmin.equals', { defaultValue: 'Equals' })}</th>
                <th>{t('layerAdmin.from', { defaultValue: 'From' })}</th>
                <th>{t('layerAdmin.to', { defaultValue: 'To (excl.)' })}</th>
                <th>{t('layerAdmin.fill', { defaultValue: 'Fill' })}</th>
                {LANGUAGES.map((l) => <th key={l}>{t('layerAdmin.legend_in', { lang: l.toUpperCase() })}</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {classes.map((c, i) => (
                <tr key={i}>
                  <td><input className={input} value={c.equals ?? ''} onChange={(e) => setClass(i, { equals: e.target.value || null })} /></td>
                  <td><input type="number" className={input} value={c.from ?? ''} onChange={(e) => setClass(i, { from: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                  <td><input type="number" className={input} value={c.to ?? ''} onChange={(e) => setClass(i, { to: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                  <td><input type="color" value={c.fill ?? '#2f6bff'} onChange={(e) => setClass(i, { fill: e.target.value })} /></td>
                  {LANGUAGES.map((l) => (
                    <td key={l}><input className={input} value={c.labels?.[l] ?? ''} onChange={(e) => setClass(i, { labels: { ...(c.labels ?? {}), [l]: e.target.value } })} /></td>
                  ))}
                  <td><button type="button" className={button} onClick={() => onChange({ classes: classes.filter((_, j) => j !== i) })}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className={button} onClick={() => onChange({ classes: [...classes, { fill: '#2f6bff' }] })}>
            {t('layerAdmin.add_class', { defaultValue: 'Add a class' })}
          </button>
        </div>
      )}

      {style.mode === 'named' && (
        <label className="block w-96">
          <span className="text-sm">{t('layerAdmin.style_name', { defaultValue: 'GeoServer style' })}</span>
          <input className={input} value={style.sld ?? ''} onChange={(e) => onChange({ sld: e.target.value })} />
        </label>
      )}

      {style.mode === 'sld' && (
        <textarea className={`${input} font-mono text-xs`} rows={18} value={style.sld ?? ''} onChange={(e) => onChange({ sld: e.target.value })} />
      )}

      {preview !== null && (
        <pre className="text-xs whitespace-pre-wrap break-all bg-gn-surface p-2 rounded max-h-96 overflow-auto">
          {preview || t('layerAdmin.no_sld', { defaultValue: 'GeoServer keeps its own style.' })}
        </pre>
      )}
    </div>
  );
};

const Colour: React.FC<{ label: string; value?: string | null; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <label className="block">
    <span className="text-sm">{label}</span>
    <div className="flex gap-2 items-center">
      <input type="color" value={value ?? '#2f6bff'} onChange={(e) => onChange(e.target.value)} />
      <input className={input} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="#2f6bff" />
    </div>
  </label>
);

const MeasuresEditor: React.FC<{
  measures: Measure[];
  fields: LayerField[];
  methods: string[];
  onChange: (m: Measure[]) => void;
  t: T;
  /** The layer says none: catchments add up what its columns suggest. */
  suggested: boolean;
  onSuggest: () => Promise<void>;
}> = ({ measures, fields, methods, onChange, t, suggested, onSuggest }) => {
  const setOne = (i: number, patch: Partial<Measure>) => onChange(measures.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const [busy, setBusy] = React.useState(false);
  const suggest = async () => {
    if (measures.length > 0 && !window.confirm(t('layerAdmin.suggest_replace', { defaultValue: 'Replace these measures with what the columns suggest?' }))) {
      return;
    }
    setBusy(true);
    try {
      await onSuggest();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <p className="text-sm text-gn-muted">
        {t('layerAdmin.measures_hint', { defaultValue: 'What a catchment report adds up from this layer, each feature counted by the share of it inside the catchment. Weighted measures are averaged by the weight (the population).' })}
      </p>
      {suggested && (
        <p className="text-sm rounded border border-gn-light bg-gn-tertiary px-3 py-2">
          {t('layerAdmin.measures_auto', { defaultValue: 'No measures are set: catchments add up what the columns suggest (counts are summed, percentages and averages weighted by the population). Fill them in below to see and change them.' })}
        </p>
      )}
      <div>
        <button type="button" className={button} disabled={busy || fields.length === 0} onClick={suggest}>
          {t('layerAdmin.suggest_measures', { defaultValue: 'Suggest from the columns' })}
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gn-muted">
            <th>{t('layerAdmin.column', { defaultValue: 'Column' })}</th>
            <th>{t('layerAdmin.method', { defaultValue: 'How' })}</th>
            <th>{t('layerAdmin.weight', { defaultValue: 'Weighted by' })}</th>
            <th>{t('layerAdmin.group', { defaultValue: 'Report table' })}</th>
            <th>{t('layerAdmin.position', { defaultValue: 'Position' })}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {measures.map((m, i) => (
            <tr key={i}>
              <td>
                <select className={input} value={m.field} onChange={(e) => setOne(i, { field: e.target.value })}>
                  {fields.map((f) => <option key={f.key} value={f.key}>{f.column}</option>)}
                </select>
              </td>
              <td>
                <select className={input} value={m.method} onChange={(e) => setOne(i, { method: e.target.value as Measure['method'] })}>
                  {methods.map((x) => <option key={x} value={x}>{t(`layerAdmin.method_${x}`, { defaultValue: x })}</option>)}
                </select>
              </td>
              <td>
                <select className={input} value={m.weight ?? ''} disabled={m.method === 'portion'}
                        onChange={(e) => setOne(i, { weight: e.target.value || null })}>
                  <option value="">—</option>
                  {fields.map((f) => <option key={f.key} value={f.key}>{f.column}</option>)}
                </select>
              </td>
              <td><input className={input} value={m.group ?? ''} onChange={(e) => setOne(i, { group: e.target.value || null })} /></td>
              <td><input type="number" className={input} value={m.position ?? ''} onChange={(e) => setOne(i, { position: e.target.value === '' ? null : Number(e.target.value) })} /></td>
              <td><button type="button" className={button} onClick={() => onChange(measures.filter((_, j) => j !== i))}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className={button} disabled={fields.length === 0}
              onClick={() => onChange([...measures, { field: fields[0].key, method: 'portion', position: (measures.length + 1) * 10 }])}>
        {t('layerAdmin.add_measure', { defaultValue: 'Add a measure' })}
      </button>
    </div>
  );
};

export default LayerManagement;
